/**
 * UsersSDKWrapper - SDK Wrapper for organisation user CRUD.
 *
 * Backward-compatible interface for UserApiService's user-management methods,
 * routed through the generated `users` API SDK instead of the hand-written
 * AbstractApiService axios path. Follows the NotificationsSDKWrapper pilot.
 *
 * org_id handling:
 *   The custom-axios-instance mutator injects the Bearer token and the X-ORG-ID
 *   header automatically (from getOrgId()). It does NOT add org_id to the query
 *   string or body. doc2-api's /users/get_users reads org_id from the query, and
 *   create/update read it from the form body, so this wrapper supplies org_id
 *   explicitly from the same getOrgId() helper the mutator uses. The legacy
 *   AbstractApiService relied on its own org_id injection — this keeps parity.
 *
 * Scope: getUsers, getUserDetails, addUser, editUser, deleteUser. Other
 * UserApiService methods (preferences, subscriptions, ACL, login) stay on the
 * legacy path and are unaffected — the per-method guard in UserApiService only
 * delegates the methods implemented here.
 */

import { BaseSDKWrapper } from './BaseSDKWrapper';
import { getUsers } from '../users/users';
import { getOrgId } from 'src/utils/organization/org_id_helper';

/**
 * Normalise the legacy `data` argument (FormData OR plain object) into the
 * plain key/value object the generated SDK body builders expect.
 */
function toBody(data: unknown): Record<string, any> {
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return Object.fromEntries(data.entries());
  }
  return (data as Record<string, any>) || {};
}

export class UsersSDKWrapper extends BaseSDKWrapper {
  private usersSDK: ReturnType<typeof getUsers>;

  constructor() {
    super('UsersSDK');
    this.usersSDK = getUsers();
  }

  /**
   * List org users. The legacy method took a raw query STRING
   * ("keyword=x&sort_by=email&order=asc"); we parse it into typed SDK params
   * and supply org_id from getOrgId() (required by the endpoint).
   */
  async getUsers(queryParam = ''): Promise<any> {
    this.logOperation('getUsers', { queryParam });
    const qp = new URLSearchParams(queryParam);
    const params: any = { org_id: getOrgId() };
    const keyword = qp.get('keyword');
    const sortBy = qp.get('sort_by');
    const order = qp.get('order');
    if (keyword) params.keyword = keyword;
    if (sortBy) params.sort_by = sortBy;
    if (order) params.order = order;
    return this.execute(
      () => this.usersSDK.getUsersUsersGetUsersGet(params),
      'getUsers',
    );
  }

  /** User detail with groups + sub-organisations. */
  async getUserDetailsWithGroupAndSubOrgById(id: string): Promise<any> {
    this.logOperation('getUserDetails', { id });
    return this.execute(
      () =>
        // org_id is a REQUIRED query param on doc2-api's
        // /users/get_user_with_groups_and_sub_organisations_sync (FastAPI rejects
        // the request with 422 without it). The mutator's X-ORG-ID header does
        // NOT satisfy a query param, so supply org_id explicitly — same pattern
        // as getUsers above.
        this.usersSDK.getUserDetailsUsersGetUserWithGroupsAndSubOrganisationsSyncGet(
          { user_id: id, org_id: getOrgId() } as any,
        ),
      'getUserDetails',
    );
  }

  /** Create a user. `data` is the FormData built by UserFormDialog. */
  async addUser(data: unknown): Promise<any> {
    const body = toBody(data);
    if (body.org_id === undefined) body.org_id = getOrgId();
    this.logOperation('addUser', { email: body.email });
    return this.execute(
      () => this.usersSDK.createUserUsersCreatePost(body as any),
      'addUser',
    );
  }

  /** Update a user. SDK wants a numeric userId; legacy passes a string. */
  async editUser(user_id: string, data: unknown): Promise<any> {
    const body = toBody(data);
    if (body.org_id === undefined) body.org_id = getOrgId();
    this.logOperation('editUser', { user_id });
    return this.execute(
      () =>
        this.usersSDK.updateUserUsersUpdateUserIdPut(Number(user_id), body as any),
      'editUser',
    );
  }

  /** Delete a user. org_id travels as a query param (parity with legacy). */
  async deleteUser(user_id: string): Promise<any> {
    this.logOperation('deleteUser', { user_id });
    return this.execute(
      () =>
        this.usersSDK.deleteUserUsersDeleteUserIdDelete(Number(user_id), {
          org_id: getOrgId(),
        } as any),
      'deleteUser',
    );
  }
}
